/**
 * Status pill, and the lamp inside it. Screen spec 4.1, Instrument motif 1.
 *
 * THE RULE, and it is not a polish item: the lamp is shape coded and word
 * labeled, without exception. Five shapes, three colors, and the word is never
 * dropped at any size, including in the icon rail. Turn every color off and
 * the screen still works. A lamp shipped without its adjacent word is a bug.
 *
 *   Live         solid disc                        live
 *   Attention    disc with a knocked out centre    attention  (a bullseye)
 *   Down         disc with a knocked out bar       down
 *   Building     hollow ring with a rotating arc   neutral, never a status hue
 *   Paused       hollow ring at rest               muted
 *
 * Two of the seven wire values share the resting ring: Paused and Replaced.
 * Both mean the same physical thing, nothing is running here and nothing is
 * wrong, and the word is what separates them. Inventing a sixth shape to
 * distinguish two states that need no distinguishing would weaken the four
 * shapes that carry real news.
 *
 * Deviation from screen spec 4.1, recorded: the spec gives Live an 8px bloom
 * and Deploying a pulse. Instrument bans glow outright and replaces it with
 * fill weight, so nothing here glows. The spec's own reasoning survives
 * intact, because it was never the glow that carried the state.
 *
 * Sizes are the direction's three: 8px inline, 12px in a header, 40px on the
 * overview band. The 40px lamp belongs to the traffic light alone.
 */

import { t } from '../../lib/i18n/t';
import type { StatusValue } from '../../lib/types';
import '../../styles/components/pill.css';

export type LampSize = 'sm' | 'md' | 'lg';
export type LampShape = 'disc' | 'bullseye' | 'barred' | 'arc' | 'ring';
export type LampTone = 'live' | 'attention' | 'down' | 'building' | 'idle' | 'pending';

interface LampSpec {
  shape: LampShape;
  tone: LampTone;
  /** The English string. It goes through t() at render, never before. */
  word: string;
}

const LAMPS: Record<StatusValue, LampSpec> = {
  live: { shape: 'disc', tone: 'live', word: 'Live' },
  ready: { shape: 'disc', tone: 'live', word: 'Ready' },
  // Shares Deploying's rotating arc, and that is right: both mean "we are
  // working on it, come back in a moment". The word is what separates them,
  // which is this file's whole rule.
  provisioning: { shape: 'arc', tone: 'building', word: 'Provisioning' },
  deploying: { shape: 'arc', tone: 'building', word: 'Deploying' },
  // "Failed" is a verdict on the person. "Didn't work" is a verdict on the
  // deploy, which is the thing that actually did not work.
  'didnt-work': { shape: 'bullseye', tone: 'attention', word: "Didn't work" },
  down: { shape: 'barred', tone: 'down', word: 'Down' },
  paused: { shape: 'ring', tone: 'idle', word: 'Paused' },
  replaced: { shape: 'ring', tone: 'idle', word: 'Replaced' },
  skipped: { shape: 'ring', tone: 'idle', word: 'Skipped' },
  'cleaning-up': { shape: 'arc', tone: 'building', word: 'Cleaning up' },
};

export function statusWord(value: StatusValue): string {
  return t(LAMPS[value].word);
}

const PX: Record<LampSize, number> = { sm: 8, md: 12, lg: 40 };

export type LampProps = { size?: LampSize } & (
  | { value: StatusValue; shape?: never; tone?: never }
  /**
   * An explicit shape and tone, for a state that is NOT a project status. A
   * custom domain waiting on somebody's DNS is a real state with a real lamp,
   * but putting it in StatusValue would mean every switch over project status
   * in the product suddenly has a case that cannot happen to a project.
   */
  | { value?: never; shape: LampShape; tone: LampTone }
);

/** The glyph on its own. Always rendered next to its word by a caller. */
export function Lamp(props: LampProps) {
  const size = props.size ?? 'md';
  const { shape, tone } =
    props.value !== undefined
      ? LAMPS[props.value]
      : { shape: props.shape, tone: props.tone };
  const px = PX[size];

  return (
    <span className="lamp" data-shape={shape} data-tone={tone} data-size={size} aria-hidden="true">
      <svg viewBox="0 0 16 16" width={px} height={px} focusable="false">
        {shape === 'disc' ? <circle cx="8" cy="8" r="7" className="lamp__fill" /> : null}

        {shape === 'bullseye' ? (
          <path
            className="lamp__fill"
            fillRule="evenodd"
            d="M8 1a7 7 0 1 1 0 14A7 7 0 0 1 8 1Zm0 4a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"
          />
        ) : null}

        {shape === 'barred' ? (
          <path
            className="lamp__fill"
            fillRule="evenodd"
            d="M8 1a7 7 0 1 1 0 14A7 7 0 0 1 8 1ZM2.6 6.5h10.8v3H2.6Z"
          />
        ) : null}

        {shape === 'arc' || shape === 'ring' ? (
          <circle cx="8" cy="8" r="6" className="lamp__ring" fill="none" />
        ) : null}

        {shape === 'arc' ? (
          <path
            className="lamp__arc"
            d="M8 2a6 6 0 0 1 6 6"
            fill="none"
            strokeLinecap="round"
          />
        ) : null}
      </svg>
    </span>
  );
}

export interface StatusPillProps {
  value: StatusValue;
  /** sm is 8px and inline. md is 12px and the default. lg is the band lamp. */
  size?: Exclude<LampSize, 'lg'>;
  /**
   * True when this value can change while the page is open. It turns the pill
   * into a polite live region, so a deploy that finishes is announced rather
   * than silently redrawn.
   */
  live?: boolean;
}

export function StatusPill({ value, size = 'md', live = false }: StatusPillProps) {
  return (
    <span
      className="pill"
      data-size={size}
      data-tone={LAMPS[value].tone}
      role={live ? 'status' : undefined}
      aria-live={live ? 'polite' : undefined}
    >
      <Lamp value={value} size={size} />
      <span className="pill__word">{statusWord(value)}</span>
    </span>
  );
}
